import { useLocation } from 'react-use';
import { WarningText } from '@hungryroot/pho';
import { type ProductDetail } from '@hungryroot/rest';
import { tags as ProductTags } from '@hungryroot/tags';
import { Paragraph } from '@hungryroot/ui-kit';
import { useItemWarning } from '../../hooks/useItemWarning';
import { usePdpSubscriptionBanner } from '../../hooks/usePdpSubscriptionBanner';
import { getBrandTagId } from '../../utils/getBrandTagId';
import { AdditionalProductInfo } from '../AdditionalProductInfo/AdditionalProductInfo';
import { Divider } from '../Divider/Divider';
import { Header } from '../Header/Header';
import { Ingredients } from '../Ingredients/Ingredients';
import { Nutrition } from '../Nutrition/Nutrition';
import {
  formatNutritionValue,
  nutritionDataToNutritionTable,
  stringNumberToNumberNoDecimal,
} from '../Nutrition/utils';
import { OSNSection } from '../OSNSection/OSNSection';
import { getProteinSourcingVariants } from '../ProteinSourcingDisclosure/getProteinSourcingVariants';
import { RDPModal } from '../RDPModal/RDPModal';
import { SupplementNutrition } from '../SupplementNutrition/SupplementNutrition';
import { XButton } from '../XButton/XButton';
import { type SimplePDPModalContentsProps } from './types';

import styles from './SimplePDPModal.module.css';

function useShowNutritionHtml({ productV2 }: { productV2?: ProductDetail }) {
  const { pathname } = useLocation();

  if (!productV2) return false;

  const { primary_category_name: primaryCategoryName } = productV2;

  const pathIsLimitedTime = pathname === '/limited-time';

  const isMeatOrProduce =
    primaryCategoryName === 'Produce' ||
    primaryCategoryName === 'Meat + Seafood';

  const isLimitedTimeProduceOrMeat = pathIsLimitedTime && isMeatOrProduce;
  const isSupplement = primaryCategoryName === 'Supplements';

  return isSupplement || isLimitedTimeProduceOrMeat;
}

export function SimplePDPModalContents(props: SimplePDPModalContentsProps) {
  const {
    testid,
    onDismiss,
    footerButton,
    header,
    overlayClassName,
    primaryTopIconButton,
    secondaryTopIconButton,
    productV2,
    useServingUnits,
    warningComponentOverride,
    onImageScroll,
    showOSNSection = false,
    enableFoodProfileNeverWarnings = false,
    excludeCredits,
    onProductContentScroll,
    onShowMoreNutrition,
    onProductXClick,
    recipesSection,
    badgeLabel,
    isPdpBadgeLabelResolved = true,
  } = props;

  const isLoading =
    productV2 === undefined || isPdpBadgeLabelResolved === false;
  const name = productV2?.name || '';
  const imageUrl = productV2?.card_image_url;
  const numRatings = productV2?.num_ratings;

  // Dev note: pre-checkout, we want `serving_units` to be the servings. post-checkout this is no longer true
  const servings =
    useServingUnits && productV2?.serving_units
      ? productV2.serving_units
      : productV2?.servings;

  const nutrition = productV2?.nutrition;
  const packagedImageUrl = productV2?.packaged_image2_url;
  const badges = productV2?.badges;
  const rating = productV2?.product_score;
  const brandName = productV2?.brand_name;
  const remark = productV2?.remark;
  const instruction = productV2?.instruction;
  const tags = productV2?.tags;
  const description = productV2?.description;
  const ingredientsHtml = productV2?.ingredients_html;
  const nutritionHtml = productV2?.nutrition_html;
  const brandId = getBrandTagId(brandName, tags);

  const proteinSourcing = getProteinSourcingVariants(tags);

  const showNutritionHtml = useShowNutritionHtml({
    productV2,
  });

  const subscriptionBanner = usePdpSubscriptionBanner(productV2?.id);

  const warnings = useItemWarning({
    product: productV2,
    enableFoodProfileNeverWarnings,
  });

  const heroImageAvailabilityBadge =
    badgeLabel === 'coming_soon' || badgeLabel === 'out_of_stock'
      ? badgeLabel
      : null;

  const getWarningComponent = () => {
    if (warningComponentOverride) {
      return warningComponentOverride;
    }

    if (warnings.length > 0) {
      return (
        <>
          {warnings.map((warning) => (
            <WarningText
              key={warning.kind}
              intensity={warning.warningTextIntensity}
              testid={
                warning.kind === 'dietary' || warnings.length === 1
                  ? 'warning-text'
                  : `warning-text-${warning.kind}`
              }
            >
              {warning.warningText}
            </WarningText>
          ))}
        </>
      );
    }

    return subscriptionBanner;
  };

  // Calculate discount values
  const credits = productV2?.points ? Number(productV2.points) : undefined;

  return (
    <RDPModal
      testid={testid}
      credits={excludeCredits === true ? undefined : credits}
      pointsAfterDiscount={productV2?.points_after_discount ?? undefined}
      discount={productV2?.discount ?? undefined}
      discountPercentage={productV2?.discount_percentage ?? undefined}
      overlayClassName={overlayClassName}
      brandName={brandName}
      brandId={brandId}
      isLoading={isLoading}
      ariaLabel={name}
      isOpen={true}
      imageUrl={imageUrl === null ? undefined : imageUrl}
      packagedImageUrl={packagedImageUrl}
      title={name}
      primaryTopRightIconButton={
        <XButton
          onClick={() => {
            onProductXClick?.();
            onDismiss();
          }}
        />
      }
      primaryTopIconButton={primaryTopIconButton}
      secondaryTopIconButton={secondaryTopIconButton}
      warningComponent={getWarningComponent()}
      onImageScroll={onImageScroll}
      onProductContentScroll={onProductContentScroll}
      heroImageAvailabilityBadge={heroImageAvailabilityBadge}
      productInfo={[
        {
          label: 'Rating',
          value: rating
            ? `${stringNumberToNumberNoDecimal(
                rating.toString()
              )}% (${numRatings})`
            : 'New',
        },
        {
          label: 'Serving',
          value: `${servings}`,
        },
        {
          label: 'Cal per serv.',
          value: `${nutrition ? nutrition.calories : ''}`,
        },
      ]}
      badges={badges}
      badgesOverride={props.badgesOverride}
      onDismiss={onDismiss}
      header={
        header ?? (
          <Header
            primaryTopRightIconButton={
              <XButton
                onClick={() => {
                  onProductXClick?.();
                  onDismiss();
                }}
                size="medium"
                prominence="tertiary"
                style="muted"
              />
            }
          />
        )
      }
    >
      <AdditionalProductInfo
        description={description}
        refrigerate={Boolean(
          !tags?.find((tag) => tag.id === ProductTags.NoRefrigeration)
        )}
        instructions={instruction}
        tip={remark}
        proteinSourcing={proteinSourcing}
      />
      {productV2 && showOSNSection ? (
        <OSNSection
          productId={productV2.id}
          productTags={productV2.tags}
          enableFoodProfileNeverOverride={enableFoodProfileNeverWarnings}
        />
      ) : null}
      {showNutritionHtml && nutritionHtml ? (
        <>
          <SupplementNutrition nutritionHtml={nutritionHtml} />
          <Divider />
        </>
      ) : (
        <>
          <Nutrition
            onShowMoreNutrition={onShowMoreNutrition}
            servings={
              <>
                <Paragraph>Servings per container: {servings}</Paragraph>
                {nutrition?.serving_size_text && (
                  <Paragraph className={styles['amount-per-serving']}>
                    Serving size: {nutrition.serving_size_text}
                  </Paragraph>
                )}
              </>
            }
            productInfo={[
              {
                label: 'Calories',
                value: `${
                  nutrition
                    ? stringNumberToNumberNoDecimal(
                        nutrition.calories.toString()
                      )
                    : ''
                }`,
              },
              {
                label: 'Fat',
                value: `${
                  nutrition
                    ? formatNutritionValue(nutrition.total_fat, 'total_fat')
                    : ''
                }`,
              },
              {
                label: 'Carbs',
                value: `${
                  nutrition
                    ? formatNutritionValue(nutrition.total_carb, 'total_carb')
                    : ''
                }`,
              },
              {
                label: 'Fiber',
                value: `${
                  nutrition
                    ? formatNutritionValue(nutrition.fiber, 'fiber')
                    : ''
                }`,
              },
              {
                label: 'Protein',
                value: `${
                  nutrition
                    ? formatNutritionValue(nutrition.protein, 'protein')
                    : ''
                }`,
              },
              {
                label: 'Sugars',
                value: `${
                  nutrition
                    ? formatNutritionValue(nutrition.sugars, 'sugars')
                    : ''
                }`,
              },
            ]}
            sections={nutrition ? nutritionDataToNutritionTable(nutrition) : []}
            disclaimer="The % Daily Value (DV) is based on a 2,000 calorie diet."
          />
          <Divider />
        </>
      )}
      {ingredientsHtml && (
        <>
          <Ingredients ingredients={ingredientsHtml} brandName={brandName} />
          <Divider />
        </>
      )}
      {recipesSection}
      {footerButton}
    </RDPModal>
  );
}
